/**
 * Shared exponential-backoff-with-jitter calculation for client-side retry logic.
 * Mirrors api/utils/retryHelper.js's backoff formula so backend and frontend
 * retry behavior stay conceptually aligned (see #353 Phase 4).
 */
import { Milliseconds } from 'types/units';

const DEFAULT_MAX_DELAY_MS: Milliseconds = 30000;

/**
 * Calculates exponential backoff delay with jitter.
 * @param attempt - Current attempt number (0-indexed)
 * @param baseDelay - Base delay in milliseconds
 * @param maxDelay - Upper bound on the returned delay (default 30s)
 * @returns Delay in milliseconds, jittered ±25% and capped at maxDelay
 */
export function calculateBackoffDelay(
  attempt: number,
  baseDelay: Milliseconds,
  maxDelay: Milliseconds = DEFAULT_MAX_DELAY_MS
): Milliseconds {
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
  return Math.min(exponentialDelay + jitter, maxDelay);
}
